import {IS_EXCLUDED_COLUMN_DEF, ROW_ID_COLUMN_DEF, SystemColumnName} from 'data-frames/DataFrameConstants';
import {DataFrame, DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {JSONValue} from 'util/Types';

import findAndAssertColumn from './findAndAssertColumn';
import validateColumnType, {ColumnTypeValidationOptions} from './validateColumnType';

export type DataFrameValueAccessor<TOutput = JSONValue> = (row: Record<string, JSONValue>) => TOutput;

export function createUnvalidatedAccessor(columnDef: DataFrameColumnDef): DataFrameValueAccessor {
  return (row) => row[columnDef.systemName];
}

/** Accessor for IS_EXCLUDED system column.
 *
 * System columns as a concept is deprecated. Do not use in new code.
 *
 * @deprecated TODO(BNCH-62118): Remove system columns from the front-end.
 */
export const isExcludedAccessor = createUnvalidatedAccessor(
  IS_EXCLUDED_COLUMN_DEF
) as DataFrameValueAccessor<boolean>;
/** Accessor for ROW_ID system column.
 *
 * System columns as a concept is deprecated. Do not use in new code.
 *
 * @deprecated TODO(BNCH-62118): Remove system columns from the front-end.
 */
export const rowIdAccessor = createUnvalidatedAccessor(ROW_ID_COLUMN_DEF) as DataFrameValueAccessor<string>;

/** Map of system columns to their accessors.
 *
 * System columns as a concept is deprecated. Do not use in new code.
 *
 * @deprecated TODO(BNCH-62118): Remove system columns from the front-end.
 */
// Don't type as `Record<string, DataFrameValueAccessor>`, so callers can access the more specific types
// of `IS_EXCLUDED_ACCESSOR` and `ROW_ID_ACCESSOR`
export const SYSTEM_COLUMN_ACCESSORS = {
  isExcluded: isExcludedAccessor,
  rowId: rowIdAccessor,
};

/** Map of system columns to leaf types.
 *
 * System columns as a concept is deprecated. Do not use in new code.
 *
 * @deprecated TODO(BNCH-62118): Remove system columns from the front-end.
 */
export const SYSTEM_COLUMN_LEAF_TYPES = {
  isExcluded: IS_EXCLUDED_COLUMN_DEF.type.leafType,
  rowId: ROW_ID_COLUMN_DEF.type.leafType,
};

export type SystemColumnAccessorName = 'rowId' | 'isExcluded';

/**
 * @deprecated TODO(BNCH-62118): Remove system columns from the front-end.
 */
// This object enforces, via typing, that all system columns are accounted for.
// If a new system column is created, add it to `SYSTEM_COLUMN_ACCESSORS` and `SYSTEM_COLUMN_LEAF_TYPES`
export const SYSTEM_COLUMN_TO_ACCESSOR_NAME: {
  [key in SystemColumnName]: SystemColumnAccessorName;
} = {
  [SystemColumnName.IS_EXCLUDED]: 'isExcluded',
  [SystemColumnName.ROW_ID]: 'rowId',
};

/**
 * Create an accessor function for a column in a data frame.
 *
 * The returned accessor is used to access a value from a data frame row, without explicitly passing around
 * the name of the data frame column.
 *
 * For example, to create and use the accessor for a data frame's "dose" column:
 *
 *    ```
 *    const {accessor} = createAccessors(df, 'dose', {allowedLeafTypes: [LeafType.BOOLEAN]})
 *    const doseValues = df.rows.map(row => accessor(row))
 *    ```
 *
 * @param dataFrame The data frame.
 * @param columnSystemName The system name of the column for which the accessor is being created.
 * @param columnTypeValidationOptions Type validation options for the column. If options are not passed
 *                                    for a column, any column type will be allowed.
 * @returns {accessor, validatedLeafType} Accessor function and validated leaf type for the given column.
 * @throws ColumnTypeValidationOptions If a column type is invalid
 */
export function createAccessor(
  dataFrame: DataFrame,
  columnSystemName: string,
  columnTypeValidationOptions?: ColumnTypeValidationOptions
): {
  accessor: DataFrameValueAccessor;
  validatedLeafType: LeafType;
} {
  const columnDef = findAndAssertColumn(dataFrame, columnSystemName);
  const validatedLeafType =
    columnTypeValidationOptions != null
      ? validateColumnType(columnDef.type, columnTypeValidationOptions)
      : columnDef.type.leafType;
  const accessor = createUnvalidatedAccessor(columnDef);

  return {accessor, validatedLeafType};
}

/**
 * Create accessor functions for a data frame.
 *
 * The returned accessors are used to access a value from a data frame row, without explicitly passing
 * around the name of the data frame column.
 *
 * For example, to use a data frame's "dose" column for "x":
 *
 *   ```
 *    try:
 *      const {accessors} = createAccessors(df, {x: 'dose'}, {x: {allowedLeafTypes: [LeafType.BOOLEAN]}})
 *      xValue = accessors.x(df.rows[0])
 *      isExcluded = accessors.isExcluded(df.rows[0])
 *    except ColumnTypeValidationException:
 *    ```
 *
 * To validate that the data frame's columns are of an expected type, pass in `columnTypeValidationOptionsMap`.
 * If a column type is invalid, a ColumnTypeValidationException will be thrown.
 * If no validation options are passed for a column, any column type is valid.
 * See `validateColumnType` for more information on column type validation and validated leaf types.
 *
 * @param dataFrame The data frame
 * @param accessorColumnsMap A map between the external names to access columns by and data frame system column names.
 * @param columnTypeValidationOptionsMap Type validation options for each column. If options are not passed
 *                                       for a column, any column type will be allowed.
 * @returns {accessors, validatedLeafTypes} Accessor functions and validated leaf types for each column.
 * @throws ColumnTypeValidationOptions If a column type is invalid
 */
export function createAccessors<
  TAccessorColumns extends Exclude<string, keyof typeof SYSTEM_COLUMN_ACCESSORS>,
  TAccessorColumnsMap extends {[key in TAccessorColumns]?: string},
  TValidationMap extends {
    [key in TAccessorColumns & string]?: ColumnTypeValidationOptions;
  },
>(
  dataFrame: DataFrame,
  accessorColumnsMap: TAccessorColumnsMap,
  columnTypeValidationOptionsMap?: TValidationMap
): {
  accessors: {[key in keyof TAccessorColumnsMap]: DataFrameValueAccessor} & typeof SYSTEM_COLUMN_ACCESSORS;
  validatedLeafTypes: {[key in keyof TAccessorColumnsMap]: LeafType} & typeof SYSTEM_COLUMN_LEAF_TYPES;
} {
  let accessors = {};
  let validatedLeafTypes = {};

  Object.entries(accessorColumnsMap).forEach(([dataColumn, columnSystemName]: [TAccessorColumns, string]) => {
    const leafTypeValidationOptions = columnTypeValidationOptionsMap?.[dataColumn];
    const {accessor, validatedLeafType} = createAccessor(
      dataFrame,
      columnSystemName,
      leafTypeValidationOptions
    );

    accessors = {...accessors, [dataColumn]: accessor};
    validatedLeafTypes = {...validatedLeafTypes, [dataColumn]: validatedLeafType};
  });

  // Add system columns, which must exist
  accessors = {...accessors, ...SYSTEM_COLUMN_ACCESSORS};
  validatedLeafTypes = {...validatedLeafTypes, ...SYSTEM_COLUMN_LEAF_TYPES};

  return {
    accessors: accessors as {
      [key in keyof TAccessorColumnsMap]: DataFrameValueAccessor;
    } & typeof SYSTEM_COLUMN_ACCESSORS,
    validatedLeafTypes: validatedLeafTypes as {
      [key in keyof TAccessorColumnsMap]: LeafType;
    } & typeof SYSTEM_COLUMN_LEAF_TYPES,
  };
}
